/* eslint-disable no-underscore-dangle */
import utils from 'utils/utils';
import Model from 'utils/model';
import config from 'player/config';
import Asset from './asset';
import Stream from './stream';
import ExcludedAssets from './excluded-assets';

function getCategorySettings(asset) {
    const category = asset.getCategory();

    const settings = {
        order: null,
        categoryId: null
    };

    const currentOrder = (category.getLastAsset().id === asset.get('id') ? 'before' : 'after');

    if (settings.order) {
        if (settings.categoryId !== category.get('id')) {
            settings.order = currentOrder;
            settings.categoryId = category.get('id');
        }
    } else {
        settings.order = currentOrder;
        settings.categoryId = category.get('id');
    }

    return settings;
}

const NextAsset = function () {};

NextAsset.prototype = {
    /**
         * Check if given asset has next stream
         *
         * @param asset
         * @returns {boolean}
         */
    hasPlayNext: function (asset, includeCategory) {
        if (asset.get('streamType') === 'live') {
            return false;
        }

        if (asset.get('nextAsset').id) {
            return true;
        }

        if (includeCategory === true) {
            return this.hasNextInCategory(asset);
        }

        return false;
    },

    /**
         * Check if given asset has next stream
         *
         * @param asset
         * @returns {boolean}
         */
    hasNextInCategory: function (asset) {
        const category = asset.getCategory();

        if (category) {
            if (category.isSeries()) {
                return true;
            }

            if (category.get('autoPlayNextAsset')) {
                return true;
            }

            if (category.hasEmbedPlayNext()) {
                return true;
            }
        }

        return false;
    },

    /**
         * Fetch next asset from asset series
         * Rejected when next asset is not found
         *
         * @param asset
         * @param settings
         * @returns {Promise.<T>}
         */
    fetchNextFromSeries: function(asset, settings) {
        const itemOffset = (settings && settings.order === 'before') ? 1 : -1;

        return new Promise((resolve, reject) => {
            let url;

            if (asset.get('season')) {
                url = `/categories/${asset.getCategory().get('id')}/seasons/${asset.get('season')}/assets`;
                utils.ajax(config.api.getUrl(asset.getVendor(), url), resolve, reject);
            } else {
                reject('seasonNotAvailable');
            }
        }).then(response => JSON.parse(response.responseText)).then(data => {
            let nextAsset = null;

            if (data && data.assets) {
                utils.each(data.assets, (item, index) => {
                    if (!nextAsset) {
                        if (item.id === asset.get('id')) {
                            nextAsset = data.assets[index + itemOffset]; // pick next asset (reverse)
                        }
                    }
                });
            }

            return nextAsset;
        }).then(nextAsset => {
            // resolve next asset only if available
            if (nextAsset) {
                return Promise.resolve({
                    asset: new Asset(nextAsset),
                    source: 'series'
                });
            }

            return Promise.reject('nextAssetNotFound');
        });
    },

    /**
         * Fetch next asset from parent category
         * Rejected when next asset is not found
         *
         * @param asset object
         * @param settings
         * @returns {Promise.<T>}
         */
    fetchNextFromCategory: function(asset, settings) {
        const order = (settings && settings.order) || 'after';
        let url = `/assets?limit=1&filter=categoryId::${asset.getCategory().get('id')}`;

        if (order === 'before') {
            url += `&before=${asset.get('published') / 1000 - 1}`; // time is in ms
        } else {
            url += `&sort=-published&after=${asset.get('published') / 1000 + 1}`; // time is in ms
        }

        return new Promise((resolve, reject) => {
            utils.ajax(config.api.getUrl(asset.getVendor(), url), resolve, reject);
        }).then(response => JSON.parse(response.responseText)).then(data => {
            // parse response
            if (data && data._embedded && data._embedded.assets) {
                const [nextAsset] = data._embedded.assets;

                if (nextAsset && nextAsset.id !== asset.get('id')) {
                    return nextAsset;
                }
            }

            return null;
        }).then(nextAsset => {
            // resolve next asset only if available
            if (nextAsset) {
                return Promise.resolve({
                    asset: new Asset(nextAsset),
                    source: `category-${order}`
                });
            }

            return Promise.reject('nextAssetNotFound');
        });
    },

    /**
         * Fetch next asset by providing it's id
         *
         * @param assetId
         * @returns {*}
         */
    fetchNextAssetById: function(vendor, assetId) {
        return new Asset({
            id: assetId,
            vendor: vendor
        }).fetch().then(asset => ({
            asset: asset,
            source: 'byId'
        }));
    },

    fetchNextFromMlEngine: function(asset) {
        const url = `/assets/${asset.get('id')}/related-ml?limit=20`;
        const vendor = asset.getVendor();

        return new Promise((resolve, reject) => {
            utils.ajax(config.api.getUrl(vendor, url), resolve, reject);
        }).then(response => JSON.parse(response.responseText)).then(data => {
            const assets = data && data._embedded && data._embedded.assets;
            const excludedAssets = ExcludedAssets.getAll(vendor);
            let nextAsset = null;

            // parse response
            if (assets) {
                for (let i = 0; i < assets.length; i += 1) {
                    nextAsset = assets[i] || {};

                    if (nextAsset.id !== asset.get('id') && excludedAssets.indexOf(nextAsset.id) === -1) {
                        // exclude asset from recommended list
                        ExcludedAssets.add(asset.getVendor(), nextAsset.id);
                        return nextAsset;
                    }
                }
            }

            return null;
        }).then(nextAsset => {
            // resolve next asset only if available
            if (nextAsset) {
                return Promise.resolve({
                    asset: new Asset(nextAsset),
                    source: 'ml-engine'
                });
            }
            // fallback for fetching from "old api"
            return this.fetchSimple(asset);
        });
    },

    /**
         * Fetch next for given asset
         * Cascade  lookup from direct linkage (nextAsset.id) to retriving from category
         * Rejected when nextAsset is not found
         *
         * @param asset
         * @returns {Promise}
         */
    fetchFromMlEngine: function(asset, options) {
        let request = null;
        const threshold = (options && options.mlThreshold) || 0.5;

        // check if next asset is set in api
        const nextAssetId = (asset.get('nextAsset') && asset.get('nextAsset').id) || false;

        const category = asset.getCategory();
        const settings = getCategorySettings(asset);

        if (nextAssetId) {
            // here we compare results from hand set next
            if (Math.random() > threshold) {
                request = this.fetchNextAssetById(asset.getVendor(), nextAssetId);
            } else {
                request = this.fetchNextFromMlEngine(asset);
            }
        } else if (category.isSeries()) {
            if (settings.order === 'before') {
                request = this.fetchNextFromSeries(asset, settings);
            } else {
                request = this.fetchNextFromCategory(asset, settings);
            }
        } else if (asset.get('streamType') !== 'live') {
            request = this.fetchNextFromMlEngine(asset);
        }

        return request;
    },
    /**
         * Fetch next for given asset
         * Cascade  lookup from direct linkage (nextAsset.id) to retriving from category
         * Rejected when nextAsset is not found
         *
         * @param asset
         * @returns {Promise}
         */
    fetchSimple: function(asset) {
        const category = asset.getCategory();
        const settings = getCategorySettings(asset);
        let request = null;

        if (this.hasPlayNext(asset, true)) {
            if (asset.get('nextAsset').id) {
                request = this.fetchNextAssetById(asset.getVendor(), asset.get('nextAsset').id);
            } else if (category.isSeries()) {
                if (settings.order === 'before') {
                    request = this.fetchNextFromSeries(asset, settings);
                } else {
                    request = this.fetchNextFromCategory(asset, settings);
                }
            } else if (category && (category.get('autoPlayNextAsset'))) {
                request = this.fetchNextFromCategory(asset, settings);
            }
        }

        return request;
    },

    /**
         * Fetch next for given asset
         * Cascade  lookup from direct linkage (nextAsset.id) to retriving from category
         * Rejected when nextAsset is not found
         *
         * @param asset
         * @returns {Promise}
         */
    fetch: function (asset, options) {
        let request = null;

        if (options.useMlEngine === true) {
            request = this.fetchFromMlEngine(asset, options);
        } else {
            request = this.fetchSimple(asset);
        }

        return new Promise((resolve, reject) => {
            if (request) {
                request.then(data => {
                    if (data && data.asset) {
                        const stream = new Stream(data.asset.attributes);

                        if (stream.isActive()) {
                            resolve(data);
                        } else {
                            reject('nextAssetNotActive');
                        }
                    } else {
                        reject('nextAssetNotActive');
                    }
                }, reject);
            } else {
                reject('nextAssetNotFound');
            }
        });
    }
};

utils.extend(NextAsset.prototype, Model);

export default NextAsset;
